'use client';

import { c as _c } from "react/compiler-runtime";
import './giving-history.scss';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { useReadyForQueries, useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { useGivingHistory } from '@sola/supporter-sdk/hooks/giving-history.ts';
import { commitmentsQuery, pledgesQuery } from '@sola/supporter-sdk/queries.ts';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { PaymentIssueAlert } from '../payment-issue-alert/payment-issue-alert.tsx';
import { GivingHistoryMonth } from './giving-history-month.tsx';
import { getHistoryItemsByMonth, getYearList } from './giving-history.logic.ts';

/**
 * Get component data
 */
export const useComponentData = () => {
  const {
    data,
    isFetching: commitmentsFetching,
    isFetched: commitmentsFetched
  } = useSupporterQuery([commitmentsQuery, pledgesQuery]);
  const {
    t
  } = useTranslation('my');
  const {
    isReady
  } = useReadyForQueries();
  const [commitments = [], pledges = []] = data;
  const [year, setYear] = useState(new Date().getFullYear());
  const {
    data: givingHistoryItems,
    isLoading,
    isFetching,
    isPending,
    isError
  } = useGivingHistory(year);
  /** NOTE: Could be configured in CMS */
  const numYearsToDisplay = 7;

  /**
   * Handle year change.
   */
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(Number(e.target.value));
  };

  /**
   * Group and format items for rendering.
   */
  const groupedItems = useMemo(() => {
    if (!givingHistoryItems?.length) {
      return null;
    }
    const grouped = givingHistoryItems.length > 0 ? getHistoryItemsByMonth(year, givingHistoryItems, commitments, pledges, t) : [];
    return grouped;
    // We want to run logic either when item date change or when commitments initially load.
  }, [givingHistoryItems?.[0]?.recordDate, commitmentsFetched]);
  const hasQueryResults = !(isLoading || isFetching || isPending || commitmentsFetching) || isError;
  const isShowingSkeletons = !isReady || isFetching || !hasQueryResults;
  return {
    isShowingSkeletons,
    yearList: getYearList(new Date(), numYearsToDisplay),
    hasData: !!givingHistoryItems?.length && groupedItems,
    hasError: isError,
    groupedItems,
    handleYearChange
  };
};

/**
 * Displays list of commitments based on frequency.
 */
export const GivingHistory = () => {
  const $ = _c(34);
  const {
    t
  } = useTranslation("my");
  const {
    isShowingSkeletons,
    hasData,
    hasError,
    groupedItems,
    yearList,
    handleYearChange
  } = useComponentData();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PrimerError keysToAlert={["commitments"]} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== isShowingSkeletons) {
    t1 = !isShowingSkeletons && <PaymentIssueAlert onlyShowMissingPayment={true} />;
    $[1] = isShowingSkeletons;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("givingHistory.title");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== yearList) {
    t3 = yearList.map(_temp);
    $[5] = yearList;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== handleYearChange || $[8] !== isShowingSkeletons || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex flex-col gap-snug-lg"><div className="GivingHistory__yearselect"><Select label={t2} items={t3} onChange={handleYearChange} disabled={isShowingSkeletons} /></div></div>;
    $[7] = handleYearChange;
    $[8] = isShowingSkeletons;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== isShowingSkeletons) {
    t5 = isShowingSkeletons && <GivingHistoryMonth isLoading={true} />;
    $[12] = isShowingSkeletons;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== groupedItems || $[15] !== hasData || $[16] !== isShowingSkeletons) {
    t6 = !isShowingSkeletons && hasData && groupedItems.map(_temp2);
    $[14] = groupedItems;
    $[15] = hasData;
    $[16] = isShowingSkeletons;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== hasData || $[19] !== hasError || $[20] !== isShowingSkeletons || $[21] !== t) {
    t7 = !isShowingSkeletons && !hasData && !hasError && <Alert message={t("givingHistory.noHistory")} />;
    $[18] = hasData;
    $[19] = hasError;
    $[20] = isShowingSkeletons;
    $[21] = t;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== hasError || $[24] !== isShowingSkeletons || $[25] !== t) {
    t8 = !isShowingSkeletons && hasError && <Alert title={t("givingHistory.requestError.title")} message={t("givingHistory.requestError.message")} variant="negative" action={<Link href={t("ctas.contactSupport.url")}>{t("ctas.contactSupport.text")}</Link>} />;
    $[23] = hasError;
    $[24] = isShowingSkeletons;
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t1 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <div className="max-w-md flex flex-col gap-comfy-xl">{t0}{t1}{t4}{t5}{t6}{t7}{t8}</div>;
    $[27] = t1;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
function _temp(y) {
  return {
    value: y.toString()
  };
}
function _temp2(givingMonthGroup) {
  return <GivingHistoryMonth key={givingMonthGroup.heading} {...givingMonthGroup} />;
}